import { useCallback, useEffect, useRef, useState } from "react";
import { milestones as milestonesApi } from "@/lib/api";

const STORAGE_KEY_PREFIX = "prairie:milestones-queue:";

/**
 * useMilestones — reactive queue of celebration moments.
 *
 * Contract:
 *   const { current, dismiss, checkNow } = useMilestones(userId);
 *   • `current`  — the milestone at the head of the queue, or null.
 *   • `dismiss()` — pop it and advance to the next.
 *   • `checkNow()` — call `/api/milestones/check` and enqueue any newly
 *     unlocked milestones. Safe to call after every plant save / share
 *     create; the endpoint is idempotent so this cannot double-fire.
 *
 * Queue is persisted to sessionStorage so a full-page refresh mid-moment
 * doesn't swallow an unlock the user hasn't seen yet. Cleared when the
 * user signs out (`userId` changes).
 */
export function useMilestones(userId) {
  const [queue, setQueue] = useState([]);
  // Guards against concurrent check() fan-out — React StrictMode double-
  // invokes effects in dev, and multiple bulk-save callers may race the
  // Result page's mount effect. Without this, two calls can hit the endpoint
  // before either has persisted, and both return the same `newly_unlocked`
  // list → the user sees each celebration twice.
  const inFlightRef = useRef(false);

  // Load persisted queue when user changes.
  useEffect(() => {
    if (!userId) {
      setQueue([]);
      return;
    }
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY_PREFIX + userId);
      setQueue(raw ? JSON.parse(raw) : []);
    } catch {
      setQueue([]);
    }
  }, [userId]);

  // Persist whenever the queue changes so a refresh doesn't lose unlocks.
  useEffect(() => {
    if (!userId) return;
    try {
      if (queue.length > 0) {
        sessionStorage.setItem(STORAGE_KEY_PREFIX + userId, JSON.stringify(queue));
      } else {
        sessionStorage.removeItem(STORAGE_KEY_PREFIX + userId);
      }
    } catch {
      // Ignore quota errors — celebration is nice-to-have, not critical.
    }
  }, [queue, userId]);

  const checkNow = useCallback(async () => {
    if (!userId) return [];
    if (inFlightRef.current) return [];
    inFlightRef.current = true;
    try {
      const { data } = await milestonesApi.check();
      const newly = data?.newly_unlocked || [];
      if (newly.length > 0) {
        setQueue((prev) => [...prev, ...newly]);
      }
      return newly;
    } catch {
      // Silent — the toast is delightful, not load-bearing. Never surface an
      // error dialog for a background milestone check.
      return [];
    } finally {
      inFlightRef.current = false;
    }
  }, [userId]);

  const dismiss = useCallback(() => {
    setQueue((prev) => prev.slice(1));
  }, []);

  return { current: queue[0] || null, dismiss, checkNow, queueLength: queue.length };
}
